import React, { useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { FaEye } from 'react-icons/fa';
import { MdCancel, MdEdit } from 'react-icons/md';
import { useAuth } from '../../context/AuthContext';
import {
  useCreateLeaveApplyMutation,
  useGetLeaveAppliesQuery,
  useUpdateLeaveApplyMutation,
  useUpdateLeaveApplyStatusMutation,
} from '../../redux/leaveApi';
import { getServerPagination } from '../../utils/pagination';
import { parseApiLeaveSummary, formatDayTypeLabel } from '../../utils/leaveSummary';
import { buildLeaveFormFromRecord } from '../../utils/leaveForm';
import {
  canSeeTeamLeaveList,
  getLeaveRowActionFlags,
  hasCanLeaveAccess,
  leaveApplyStatusForRole,
} from '../../utils/leaveAccess';
import { LEAVE_STATUS_FILTER_OPTIONS, DEFAULT_LEAVE_LIST_STATUS_FILTER } from '../../utils/leaveStatus';
import { ApiLoadState, ApiLoadTableRow } from './ApiLoadState';
import LeaveBalanceSummary from './LeaveBalanceSummary';
import LeaveStatusBadge from './LeaveStatusBadge';
import MetricCard from './MetricCard';
import { FilterToggleButton } from './FilterToggleButton';
import TablePagination from './TablePagination';

const LEAVE_TYPE_OPTIONS = [
  { value: 'EL', label: 'EL - Earned Leave' },
  { value: 'SL', label: 'SL - Sick Leave' },
  { value: 'CL', label: 'CL - Casual Leave' },
  { value: 'LOP', label: 'LOP - Loss Of Pay' },
];

const DAY_TYPE_OPTIONS = [
  { value: 'full_day', label: 'Full Day' },
  { value: 'first_half', label: 'First Half' },
  { value: 'second_half', label: 'Second Half' },
];

const LEAVE_DURATION_OPTIONS = [
  { value: 'single', label: 'Single Day' },
  { value: 'multiple', label: 'Multiple Days' },
];

const formInputClasses =
  'mt-1 w-full rounded-lg border border-gray-200 bg-[#faf8f5] px-3 py-2 text-sm text-gray-900 focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand';
const formReadonlyClasses =
  'mt-1 w-full rounded-lg border border-gray-200 bg-[#faf8f5] px-3 py-2 text-sm text-gray-900';

function formatLocalDate(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function currentMonthRange() {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  return {
    start: formatLocalDate(start),
    end: formatLocalDate(end),
  };
}

function currentFyRange() {
  const now = new Date();
  const year = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
  return {
    start: new Date(year, 3, 1),
    end: new Date(year + 1, 2, 31, 23, 59, 59),
  };
}

function parseLeaveDate(value) {
  if (!value) return null;
  const raw = String(value).trim();
  if (!raw) return null;

  if (/^\d{2}\/\d{2}\/\d{4}$/.test(raw)) {
    const [day, month, year] = raw.split('/').map(Number);
    const parsed = new Date(year, month - 1, day);
    if (
      !Number.isNaN(parsed.getTime()) &&
      parsed.getFullYear() === year &&
      parsed.getMonth() === month - 1 &&
      parsed.getDate() === day
    ) {
      return parsed;
    }
    return null;
  }

  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    const parsed = new Date(`${raw}T00:00:00`);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  const parsed = new Date(raw);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function formatAppliedOn(value) {
  if (!value) return '-';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);
  const day = String(parsed.getDate()).padStart(2, '0');
  const month = String(parsed.getMonth() + 1).padStart(2, '0');
  const year = parsed.getFullYear();
  return `${day}/${month}/${year}`;
}

function getLeaveDateLabel(leave) {
  const leaveStart = leave?.start_date || '-';
  const leaveEnd = leave?.end_date || leave?.start_date || '-';
  if (leaveStart !== '-' && leaveEnd !== '-' && leaveStart !== leaveEnd) {
    return `${leaveStart} → ${leaveEnd}`;
  }
  return leaveStart;
}

const iconActionBtn =
  'inline-flex shrink-0 items-center justify-center rounded-md p-1 text-red-700 hover:bg-amber-50/60 hover:text-red-800 disabled:cursor-not-allowed disabled:opacity-50';

const LEAVE_SUMMARY_METRICS = [
  { key: 'total', label: 'Total' },
  { key: 'applied', label: 'Applied' },
  { key: 'accepted', label: 'Accepted' },
  { key: 'rejected', label: 'Rejected' },
  { key: 'lopApplied', label: 'LOP Applied' },
  { key: 'available', label: 'Available' },
];

export default function LeaveManagementPanel() {
  const { user, roleType, isReportingSupervisor } = useAuth();
  const currentAccountId = Number(user?.id);
  const canReviewLeaves = hasCanLeaveAccess(roleType);
  const showTeamLeaveColumns = canSeeTeamLeaveList({ roleType, isReportingSupervisor });
  const defaultRange = useMemo(() => currentMonthRange(), []);

  const [isApplyModalOpen, setIsApplyModalOpen] = useState(false);
  const [editingLeave, setEditingLeave] = useState(null);
  const [cancelLeaveTarget, setCancelLeaveTarget] = useState(null);
  const [viewLeave, setViewLeave] = useState(null);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [statusDraft, setStatusDraft] = useState(DEFAULT_LEAVE_LIST_STATUS_FILTER);
  const [dateDraft, setDateDraft] = useState({ from: defaultRange.start, to: defaultRange.end });
  const [statusFilter, setStatusFilter] = useState(DEFAULT_LEAVE_LIST_STATUS_FILTER);
  const [dateFilter, setDateFilter] = useState({ from: defaultRange.start, to: defaultRange.end });
  const pageSize = 10;
  const leaveQuery = useMemo(() => {
    const params = { page, per_page: pageSize };
    if (showTeamLeaveColumns && search.trim()) params.search = search.trim();
    if (dateFilter.from) params.start_date = dateFilter.from;
    if (dateFilter.to) params.end_date = dateFilter.to;
    if (statusFilter && statusFilter !== 'all') params.status = statusFilter;
    return params;
  }, [page, pageSize, search, dateFilter, statusFilter, showTeamLeaveColumns]);

  const hasActiveFilters = Boolean(
    (statusFilter && statusFilter !== DEFAULT_LEAVE_LIST_STATUS_FILTER) ||
      dateFilter.from !== defaultRange.start ||
      dateFilter.to !== defaultRange.end
  );

  const [leaveForm, setLeaveForm] = useState({
    leave_duration: 'single',
    start_date: '',
    end_date: '',
    leave_type: LEAVE_TYPE_OPTIONS[2].value,
    day_type: DAY_TYPE_OPTIONS[0].value,
    description: '',
  });

  const { data: leavesRes, isFetching: isFetchingLeaves, isLoading: isLoadingLeaves, error: leavesError, refetch: refetchLeaves } =
    useGetLeaveAppliesQuery(leaveQuery);
  const [createLeaveApply, { isLoading: isCreatingLeave }] = useCreateLeaveApplyMutation();
  const [updateLeaveApply, { isLoading: isUpdatingLeave }] = useUpdateLeaveApplyMutation();
  const [updateLeaveApplyStatus, { isLoading: isUpdatingLeaveStatus }] =
    useUpdateLeaveApplyStatusMutation();

  const applyBy = useMemo(() => {
    const name = user?.name || '';
    const empCode = user?.employeeCode || '';
    return `${name}${empCode ? ` (${empCode})` : ''}`.trim();
  }, [user]);

  const leaveSummary = useMemo(() => {
    const list = leavesRes?.data || leavesRes || [];
    const apiSummary = leavesRes?.leave_summary;
    const safeList = Array.isArray(list) ? list : [];
    const hasOwnRows = safeList.some(
      (item) => Number(item?.account_id) === currentAccountId
    );
    const accountLeaves = hasOwnRows
      ? safeList.filter((item) => Number(item?.account_id) === currentAccountId)
      : safeList;
    const fyRange = currentFyRange();
    const fyLeaves = accountLeaves.filter((item) => {
      const date = parseLeaveDate(item?.start_date);
      return date && date >= fyRange.start && date <= fyRange.end;
    });

    const applied = fyLeaves.length;
    const accepted = fyLeaves.filter((item) =>
      /approved|accepted/i.test(String(item?.status || ''))
    ).length;
    const rejected = fyLeaves.filter((item) =>
      /rejected/i.test(String(item?.status || ''))
    ).length;
    const lopApplied = fyLeaves.filter((item) =>
      /lop/i.test(String(item?.leave_type || ''))
    ).length;

    const balance = parseApiLeaveSummary(apiSummary, {
      today: new Date(),
      lopUsedFallback: lopApplied,
    });

    return {
      ...balance,
      applied,
      accepted,
      rejected,
      lopApplied,
      available: balance.remaining,
    };
  }, [leavesRes, currentAccountId]);

  const visibleSummaryMetrics = useMemo(() => {
    return LEAVE_SUMMARY_METRICS.filter(
      (metric) => metric.key !== 'total' && metric.key !== 'available'
    );
  }, []);

  const leaveRows = useMemo(() => {
    const list = leavesRes?.data || leavesRes || [];
    if (!Array.isArray(list)) return [];
    return [...list].sort(
      (a, b) =>
        (parseLeaveDate(b?.start_date)?.getTime() || 0) -
        (parseLeaveDate(a?.start_date)?.getTime() || 0)
    );
  }, [leavesRes]);

  const { currentPage: serverPage, totalCount: serverTotal } = getServerPagination(
    leavesRes,
    { page, pageSize }
  );
  const hasServerPaging = serverTotal > 0;
  const currentPage = hasServerPaging ? serverPage : page;
  const totalCount = hasServerPaging ? serverTotal : leaveRows.length;
  const effectivePageSize = pageSize;
  const pageItems =
    hasServerPaging && leaveRows.length <= pageSize
      ? leaveRows
      : leaveRows.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const handleLeaveChange = (field) => (e) => {
    const value = e?.target?.value ?? '';
    setLeaveForm((prev) => {
      if (field === 'leave_duration') {
        return {
          ...prev,
          leave_duration: value,
          start_date: '',
          end_date: '',
        };
      }
      return { ...prev, [field]: value };
    });
  };

  const resetLeaveForm = () => {
    setLeaveForm((prev) => ({
      ...prev,
      leave_duration: 'single',
      start_date: '',
      end_date: '',
      description: '',
    }));
  };

  const closeApplyModal = () => {
    setIsApplyModalOpen(false);
    setEditingLeave(null);
    resetLeaveForm();
  };

  const openApplyModal = () => {
    setEditingLeave(null);
    resetLeaveForm();
    setIsApplyModalOpen(true);
  };

  const openEditLeave = (leave) => {
    setLeaveForm(
      buildLeaveFormFromRecord(leave, {
        leave_type: LEAVE_TYPE_OPTIONS[2].value,
        day_type: DAY_TYPE_OPTIONS[0].value,
      })
    );
    setEditingLeave(leave);
    setIsApplyModalOpen(true);
  };

  const handleSaveLeave = async (e) => {
    e.preventDefault();
    if (!editingLeave && Number.isNaN(currentAccountId)) return;
    if (!leaveForm.leave_type) return toast.error('Please select leave type');
    if (!leaveForm.day_type) return toast.error('Please select day type');
    if (!leaveForm.description.trim()) return toast.error('Please enter leave description');

    const isMultiple = leaveForm.leave_duration === 'multiple';
    let startDate = '';
    let endDate = '';

    if (isMultiple) {
      if (!leaveForm.start_date || !leaveForm.end_date) {
        return toast.error('Please select start and end dates');
      }
      if (new Date(leaveForm.start_date) > new Date(leaveForm.end_date)) {
        return toast.error('Invalid date range selected');
      }
      startDate = leaveForm.start_date;
      endDate = leaveForm.end_date;
    } else {
      if (!leaveForm.start_date) return toast.error('Please select leave date');
      startDate = leaveForm.start_date;
      endDate = leaveForm.start_date;
    }

    try {
      const payload = {
        start_date: startDate,
        end_date: endDate,
        leave_type: leaveForm.leave_type,
        day_type: leaveForm.day_type,
        description: leaveForm.description.trim(),
      };

      if (editingLeave?.id != null) {
        await updateLeaveApply({ id: editingLeave.id, ...payload }).unwrap();
        toast.success('Leave updated successfully.');
      } else {
        const applyStatus = leaveApplyStatusForRole(roleType);
        await createLeaveApply({
          ...payload,
          account_id: currentAccountId,
          apply_by: applyBy,
          status: applyStatus,
        }).unwrap();
        toast.success(
          applyStatus === 'approved'
            ? 'Leave applied and approved successfully.'
            : 'Leave applied successfully.'
        );
      }
      closeApplyModal();
    } catch (err) {
      toast.error(
        err?.data?.message ||
          (editingLeave ? 'Failed to update leave.' : 'Failed to apply leave.')
      );
    }
  };

  const handleLeaveStatus = async (leaveId, status) => {
    try {
      await updateLeaveApplyStatus({ id: leaveId, status }).unwrap();
      toast.success(`Leave ${status.toLowerCase()} successfully.`);
    } catch (err) {
      toast.error(err?.data?.message || `Failed to ${status.toLowerCase()} leave.`);
    }
  };

  const confirmCancelLeave = async () => {
    if (!cancelLeaveTarget?.id) return;
    try {
      await updateLeaveApplyStatus({ id: cancelLeaveTarget.id, status: 'cancelled' }).unwrap();
      toast.success('Leave cancelled successfully.');
      setCancelLeaveTarget(null);
    } catch (err) {
      toast.error(err?.data?.message || 'Failed to cancel leave.');
    }
  };

  const leaveActionFlags = (leave) =>
    getLeaveRowActionFlags({
      status: leave?.status,
      isOwnLeave: Number(leave?.account_id) === currentAccountId,
      roleType,
      isReportingSupervisor,
    });

  const renderLeaveActions = (
    leave,
    { isFinal, showCancel, showEdit, showAcceptReject },
    buttonWrapClass = 'inline-flex items-center gap-2'
  ) => (
    <div className={buttonWrapClass}>
      <button
        type="button"
        onClick={() => setViewLeave(leave)}
        className={iconActionBtn}
        aria-label="View leave"
        title="View"
      >
        <FaEye className="h-5 w-5" />
      </button>
      {showEdit ? (
        <button
          type="button"
          disabled={isUpdatingLeave || isCreatingLeave}
          onClick={() => openEditLeave(leave)}
          className={iconActionBtn}
          aria-label="Edit leave"
          title="Edit"
        >
          <MdEdit className="h-5 w-5" />
        </button>
      ) : null}
      {showAcceptReject ? (
        <>
          <button
            type="button"
            disabled={isFinal || isUpdatingLeaveStatus}
            onClick={() => handleLeaveStatus(leave.id, 'approved')}
            className="shrink-0 rounded-md bg-green-600 px-2 py-1 text-xs font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50"
          >
            Accept
          </button>
          <button
            type="button"
            disabled={isFinal || isUpdatingLeaveStatus}
            onClick={() => handleLeaveStatus(leave.id, 'rejected')}
            className="shrink-0 rounded-md bg-red-600 px-2 py-1 text-xs font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50"
          >
            Reject
          </button>
        </>
      ) : null}
      {showCancel ? (
        <button
          type="button"
          disabled={isUpdatingLeaveStatus}
          onClick={() => setCancelLeaveTarget(leave)}
          className={iconActionBtn}
          aria-label="Cancel leave"
          title="Cancel"
        >
          <MdCancel className="h-5 w-5" />
        </button>
      ) : null}
    </div>
  );

  const hasLeaveData = (leavesRes?.data || leavesRes || []).length > 0;
  const leaveListReady = hasLeaveData || (!isLoadingLeaves && !isFetchingLeaves);

  return (
    <>
      {!canReviewLeaves ? (
        <div className="mb-4">
          <LeaveBalanceSummary
            summary={leaveSummary}
            isLoading={isLoadingLeaves && !leavesRes}
          />
        </div>
      ) : null}

      {canReviewLeaves ? (
        <div className="mb-4 grid grid-cols-2 gap-3 lg:grid-cols-4 lg:gap-4">
          {visibleSummaryMetrics.map((metric) => (
            <MetricCard
              key={metric.key}
              title={metric.label}
              value={isLoadingLeaves && !leavesRes ? '—' : String(leaveSummary[metric.key] ?? 0)}
              iconSet="leave"
            />
          ))}
        </div>
      ) : null}

      <div className="rounded-xl border border-gray-100 bg-[#faf8f5] p-4 sm:p-6">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div className="min-w-0 flex-1">
            {showTeamLeaveColumns ? (
              <div className="relative w-full min-w-0 md:max-w-md">
                <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="11" cy="11" r="8" />
                    <line x1="21" y1="21" x2="16.65" y2="16.65" />
                  </svg>
                </span>
                <input
                  type="text"
                  placeholder="Search by employee name"
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(1);
                  }}
                  className="w-full rounded-lg border border-gray-200 bg-white py-2 pl-10 pr-3 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
                />
              </div>
            ) : (
              <p className="mt-1 text-xs text-gray-600">
                Default showing all leaves for the current month. Use filters for other status or dates.
              </p>
            )}
            {showTeamLeaveColumns ? (
              <p className="mt-1 hidden text-xs text-gray-600 sm:block">
                Default showing all leave requests for the current month. Use status and date filters to view other records.
              </p>
            ) : null}
          </div>
          <div className="flex shrink-0 flex-wrap items-center gap-2">
            <FilterToggleButton
              active={hasActiveFilters}
              onClick={() => setIsFilterOpen((prev) => !prev)}
              ariaLabel="Toggle leave filters"
            />
            {!canReviewLeaves ? (
              <button
                type="button"
                onClick={openApplyModal}
                className="inline-flex items-center justify-center rounded-lg bg-brand px-4 py-2 text-sm font-semibold text-white shadow-sm"
              >
                Apply Leave
              </button>
            ) : null}
          </div>
        </div>

        {isFilterOpen ? (
          <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
            <label className="flex flex-col text-sm text-gray-700">
              Status
              <select
                value={statusDraft}
                onChange={(e) => {
                  setStatusDraft(e.target.value || DEFAULT_LEAVE_LIST_STATUS_FILTER);
                }}
                className="mt-1 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
              >
                {LEAVE_STATUS_FILTER_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex flex-col text-sm text-gray-700">
              From
              <input
                type="date"
                value={dateDraft.from}
                onChange={(e) => {
                  setDateDraft((prev) => ({ ...prev, from: e.target.value }));
                }}
                className="mt-1 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
              />
            </label>
            <label className="flex flex-col text-sm text-gray-700">
              To
              <input
                type="date"
                value={dateDraft.to}
                onChange={(e) => {
                  setDateDraft((prev) => ({ ...prev, to: e.target.value }));
                }}
                className="mt-1 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
              />
            </label>
            <div className="flex items-end gap-2 sm:col-span-2 lg:col-span-2">
              <button
                type="button"
                onClick={() => {
                  setStatusFilter(statusDraft);
                  setDateFilter(dateDraft);
                  setPage(1);
                }}
                className="inline-flex w-full items-center justify-center rounded-lg bg-brand px-3 py-2 text-sm font-semibold text-white shadow-sm"
              >
                Search
              </button>
              <button
                type="button"
                onClick={() => {
                  const nextStatus = DEFAULT_LEAVE_LIST_STATUS_FILTER;
                  const nextDates = { from: defaultRange.start, to: defaultRange.end };
                  setStatusDraft(nextStatus);
                  setDateDraft(nextDates);
                  setStatusFilter(nextStatus);
                  setDateFilter(nextDates);
                  setPage(1);
                }}
                className="inline-flex w-full items-center justify-center rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Reset
              </button>
            </div>
          </div>
        ) : null}

        <ApiLoadState
          isLoading={isLoadingLeaves}
          isFetching={isFetchingLeaves}
          hasData={hasLeaveData}
          error={leavesError}
          refetch={refetchLeaves}
          message="Loading leaves..."
          className="py-6 md:hidden"
        />
        {!leavesError && leaveListReady && pageItems.length === 0 ? (
          <p className="py-6 text-center text-sm text-gray-500 md:hidden">No leave records found.</p>
        ) : !leavesError && leaveListReady && pageItems.length > 0 ? (
          <ul className="mb-4 mt-5 space-y-3 md:hidden">
            {pageItems.map((leave, index) => {
              const status = String(leave.status || 'pending');
              const { isFinal, showEdit, showCancel, showAcceptReject } = leaveActionFlags(leave);
              const dateLabel = getLeaveDateLabel(leave);
              return (
                <li
                  key={leave.id}
                  className="rounded-lg border border-gray-100 bg-white p-3 shadow-sm"
                >
                  <div className="min-w-0">
                    {showTeamLeaveColumns ? (
                      <p className="truncate text-sm font-semibold text-gray-900">
                        {leave.account_name || '-'}
                      </p>
                    ) : null}
                    <p className={`text-xs text-gray-500 ${showTeamLeaveColumns ? 'mt-1' : ''}`}>
                      #{(currentPage - 1) * pageSize + index + 1} · {dateLabel}
                    </p>
                    <div className="mt-2 space-y-1 text-xs text-gray-600">
                      <p>
                        Leave Type:{' '}
                        <span className="font-medium text-gray-800">{leave.leave_type || '-'}</span>
                      </p>
                      <p>
                        Day Type:{' '}
                        <span className="font-medium text-gray-800">{formatDayTypeLabel(leave.day_type)}</span>
                      </p>
                      <p>
                        Description:{' '}
                        <span className="font-medium text-gray-800">{leave.description || '-'}</span>
                      </p>
                      <p>
                        Applied By:{' '}
                        <span className="font-medium text-gray-800">{leave.apply_by || '-'}</span>
                      </p>
                      <p>
                        Applied On:{' '}
                        <span className="font-medium text-gray-800">
                          {formatAppliedOn(leave.created_at)}
                        </span>
                      </p>
                      <p>
                        Status: <LeaveStatusBadge status={status} />
                      </p>
                    </div>
                  </div>
                  <div className="mt-3 border-t border-gray-100 pt-3">
                    {renderLeaveActions(leave, { isFinal, showCancel, showEdit, showAcceptReject })}
                  </div>
                </li>
              );
            })}
          </ul>
        ) : null}

        <div className="mt-5 hidden overflow-x-auto md:block">
          <table className="min-w-full border-collapse text-sm">
            <thead>
              <tr>
                {showTeamLeaveColumns ? (
                  <th className="border-b border-gray-200 px-2 py-2 text-left font-semibold text-gray-700">
                    Employee
                  </th>
                ) : null}
                <th className="border-b border-gray-200 px-2 py-2 text-left font-semibold text-gray-700">
                  Applied On
                </th>
                <th className="border-b border-gray-200 px-2 py-2 text-left font-semibold text-gray-700">
                  Leave Date
                </th>
                <th className="border-b border-gray-200 px-2 py-2 text-left font-semibold text-gray-700">
                  Leave Type
                </th>
                <th className="border-b border-gray-200 px-2 py-2 text-left font-semibold text-gray-700">
                  Day Type
                </th>
                <th className="border-b border-gray-200 px-2 py-2 text-left font-semibold text-gray-700">
                  Description
                </th>
                <th className="border-b border-gray-200 px-2 py-2 text-left font-semibold text-gray-700">
                  Applied By
                </th>
                <th className="border-b border-gray-200 px-2 py-2 text-left font-semibold text-gray-700">
                  Status
                </th>
                <th className="border-b border-gray-200 px-2 py-2 text-left font-semibold text-gray-700">
                  Action
                </th>
              </tr>
            </thead>
            <tbody>
              <ApiLoadTableRow
                colSpan={showTeamLeaveColumns ? 9 : 8}
                isLoading={isLoadingLeaves}
                isFetching={isFetchingLeaves}
                hasData={hasLeaveData}
                error={leavesError}
                refetch={refetchLeaves}
                message="Loading leaves..."
              />
              {!leavesError && leaveListReady && leaveRows.length === 0 ? (
                <tr>
                  <td colSpan={showTeamLeaveColumns ? 9 : 8} className="px-2 py-5 text-center text-gray-500">
                    No leave records found.
                  </td>
                </tr>
              ) : !leavesError && leaveListReady &&
                pageItems.map((leave) => {
                  const status = String(leave.status || 'pending');
                  const { isFinal, showEdit, showCancel, showAcceptReject } = leaveActionFlags(leave);
                  const dateLabel = getLeaveDateLabel(leave);
                  return (
                    <tr key={leave.id}>
                      {showTeamLeaveColumns ? (
                        <td className="border-b border-gray-100 px-2 py-2 text-gray-800">
                          {leave.account_name || '-'}
                        </td>
                      ) : null}
                      <td className="border-b border-gray-100 px-2 py-2 text-gray-800">
                        {formatAppliedOn(leave.created_at)}
                      </td>
                      <td className="border-b border-gray-100 px-2 py-2 text-gray-800">
                        {dateLabel}
                      </td>
                      <td className="border-b border-gray-100 px-2 py-2 text-gray-800">
                        {leave.leave_type || '-'}
                      </td>
                      <td className="border-b border-gray-100 px-2 py-2 text-gray-800">
                        {formatDayTypeLabel(leave.day_type)}
                      </td>
                      <td className="border-b border-gray-100 px-2 py-2 text-gray-800">
                        {leave.description || '-'}
                      </td>
                      <td className="border-b border-gray-100 px-2 py-2 text-gray-800">
                        {leave.apply_by || '-'}
                      </td>
                      <td className="border-b border-gray-100 px-2 py-2 text-gray-800">
                        <LeaveStatusBadge status={status} />
                      </td>
                      <td className="border-b border-gray-100 px-2 py-2 whitespace-nowrap text-gray-800">
                        {renderLeaveActions(
                          leave,
                          { isFinal, showCancel, showEdit, showAcceptReject },
                          'flex flex-nowrap items-center gap-2'
                        )}
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>

        <TablePagination
          totalCount={totalCount}
          currentPage={currentPage}
          onPageChange={setPage}
          pageSize={effectivePageSize}
        />
      </div>

      {viewLeave ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={() => setViewLeave(null)}
          role="presentation"
        >
          <div
            className="w-full max-w-lg rounded-2xl bg-white px-6 py-6 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-xl font-semibold text-gray-800">Leave Details</h3>
              <button
                type="button"
                onClick={() => setViewLeave(null)}
                className="flex h-9 w-9 items-center justify-center rounded-lg text-2xl leading-none text-brand hover:bg-gray-100"
                aria-label="Close leave details"
              >
                ×
              </button>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              {viewLeave.account_name ? (
                <div className="rounded-lg border border-gray-100 bg-[#faf8f5] px-3 py-2.5">
                  <p className="text-xs text-gray-500">Employee</p>
                  <p className="mt-0.5 text-sm font-medium text-gray-900">{viewLeave.account_name}</p>
                </div>
              ) : null}
              <div className="rounded-lg border border-gray-100 bg-[#faf8f5] px-3 py-2.5">
                <p className="text-xs text-gray-500">Leave Date</p>
                <p className="mt-0.5 text-sm font-medium text-gray-900">{getLeaveDateLabel(viewLeave)}</p>
              </div>
              <div className="rounded-lg border border-gray-100 bg-[#faf8f5] px-3 py-2.5">
                <p className="text-xs text-gray-500">Leave Type</p>
                <p className="mt-0.5 text-sm font-medium text-gray-900">{viewLeave.leave_type || '-'}</p>
              </div>
              <div className="rounded-lg border border-gray-100 bg-[#faf8f5] px-3 py-2.5">
                <p className="text-xs text-gray-500">Day Type</p>
                <p className="mt-0.5 text-sm font-medium text-gray-900">{formatDayTypeLabel(viewLeave.day_type)}</p>
              </div>
              <div className="rounded-lg border border-gray-100 bg-[#faf8f5] px-3 py-2.5">
                <p className="text-xs text-gray-500">Applied By</p>
                <p className="mt-0.5 text-sm font-medium text-gray-900">{viewLeave.apply_by || '-'}</p>
              </div>
              <div className="rounded-lg border border-gray-100 bg-[#faf8f5] px-3 py-2.5">
                <p className="text-xs text-gray-500">Applied On</p>
                <p className="mt-0.5 text-sm font-medium text-gray-900">
                  {formatAppliedOn(viewLeave.created_at)}
                </p>
              </div>
              <div className="rounded-lg border border-gray-100 bg-[#faf8f5] px-3 py-2.5">
                <p className="text-xs text-gray-500">Status</p>
                <LeaveStatusBadge status={viewLeave.status} className="mt-0.5" />
              </div>
              <div className="rounded-lg border border-gray-100 bg-[#faf8f5] px-3 py-2.5 sm:col-span-2">
                <p className="text-xs text-gray-500">Description</p>
                <p className="mt-0.5 text-sm font-medium text-gray-900">
                  {viewLeave.description || '-'}
                </p>
              </div>
            </div>
            <div className="mt-5 flex justify-end">
              <button
                type="button"
                onClick={() => setViewLeave(null)}
                className="rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {!canReviewLeaves && isApplyModalOpen ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={closeApplyModal}
          role="presentation"
        >
          <div
            className="w-full max-w-2xl rounded-2xl bg-white px-6 py-6 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-xl font-semibold text-gray-800">
                {editingLeave ? 'Edit Leave' : 'Apply Leave'}
              </h3>
              <button
                type="button"
                onClick={closeApplyModal}
                className="flex h-9 w-9 items-center justify-center rounded-lg text-2xl leading-none text-brand hover:bg-gray-100"
                aria-label="Close apply leave modal"
              >
                ×
              </button>
            </div>

            <form onSubmit={handleSaveLeave} className="grid gap-4 md:grid-cols-2">
              <label className="flex flex-col text-sm text-gray-700">
                Leave Duration
                <select
                  value={leaveForm.leave_duration}
                  onChange={handleLeaveChange('leave_duration')}
                  className={formInputClasses}
                >
                  {LEAVE_DURATION_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>

              {leaveForm.leave_duration === 'multiple' ? (
                <>
                  <label className="flex flex-col text-sm text-gray-700">
                    Start Date
                    <input
                      type="date"
                      value={leaveForm.start_date}
                      onChange={handleLeaveChange('start_date')}
                      className={formInputClasses}
                    />
                  </label>
                  <label className="flex flex-col text-sm text-gray-700">
                    End Date
                    <input
                      type="date"
                      value={leaveForm.end_date}
                      onChange={handleLeaveChange('end_date')}
                      min={leaveForm.start_date || undefined}
                      className={formInputClasses}
                    />
                  </label>
                </>
              ) : (
                <label className="flex flex-col text-sm text-gray-700">
                  Leave Date
                  <input
                    type="date"
                    value={leaveForm.start_date}
                    onChange={handleLeaveChange('start_date')}
                    className={formInputClasses}
                  />
                </label>
              )}

              <label className="flex flex-col text-sm text-gray-700">
                Leave Type
                <select
                  value={leaveForm.leave_type}
                  onChange={handleLeaveChange('leave_type')}
                  className={formInputClasses}
                >
                  {LEAVE_TYPE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>

              <label className="flex flex-col text-sm text-gray-700">
                Day Type
                <select
                  value={leaveForm.day_type}
                  onChange={handleLeaveChange('day_type')}
                  className={formInputClasses}
                >
                  {DAY_TYPE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>

              <label className="flex flex-col text-sm text-gray-700">
                Apply By
                <input type="text" value={applyBy} readOnly className={formReadonlyClasses} />
              </label>

              <label className="flex flex-col text-sm text-gray-700 md:col-span-2">
                Description
                <textarea
                  rows={3}
                  value={leaveForm.description}
                  onChange={handleLeaveChange('description')}
                  placeholder="Personal work"
                  className={formInputClasses}
                />
              </label>

              <div className="flex justify-end gap-3 md:col-span-2">
                <button
                  type="button"
                  onClick={closeApplyModal}
                  className="rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isCreatingLeave || isUpdatingLeave}
                  className="rounded-lg bg-brand px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
                >
                  {isCreatingLeave || isUpdatingLeave
                    ? editingLeave
                      ? 'Saving...'
                      : 'Applying...'
                    : editingLeave
                      ? 'Save Changes'
                      : 'Apply Leave'}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}

      {cancelLeaveTarget ? (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 px-4"
          role="dialog"
          aria-modal="true"
          aria-label="Cancel leave confirmation"
          onClick={() => setCancelLeaveTarget(null)}
        >
          <div
            className="w-full max-w-md rounded-2xl bg-white p-4 shadow-2xl sm:p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-lg font-semibold text-gray-900">Cancel Leave</h2>
            <p className="mt-2 text-sm text-gray-600">
              Are you sure you want to cancel the leave?
            </p>
            <div className="mt-6 flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={() => setCancelLeaveTarget(null)}
                className="rounded-lg border border-[#e8e4df] bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-[#faf8f5]"
              >
                No
              </button>
              <button
                type="button"
                disabled={isUpdatingLeaveStatus}
                onClick={confirmCancelLeave}
                className="rounded-lg bg-brand px-4 py-2 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-60"
              >
                {isUpdatingLeaveStatus ? 'Cancelling...' : 'Yes, Cancel Leave'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
